import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon, type IconName } from '../editor/chrome/icons';
import { Avatar, Button, IconButton, SegmentedControl } from '../ui/controls';
import { Menu, type MenuEntry } from '../ui/menu';
import { tip } from '../ui/tooltip';
import {
  artSvg,
  FILES,
  filesIn,
  filesOfProject,
  ME,
  person,
  project,
  PROJECTS,
  ratioOf,
  SCOPES,
  when,
  WORKSPACE,
  type DashFile,
  type Project,
  type Scope,
} from './dashboardData';

/**
 * Six ways Artwork's home could be laid out, side by side, built from the real kit over a
 * made-up workspace — so the choice is made on the thing and not on a drawing of it.
 *
 *   /lab/dashboards?design=1..6&theme=dark|light&state=first&bare=1
 *
 * Every design holds the same four places — Recents, Owned by Me, Shared with Me and the
 * Workspace — and the workspace's Projects, and differs in which of those leads.
 *
 *   1 LIBRARY   Photos' source list and big pictures: the shape Director settled on.
 *   2 HOME      Pages' opening screen: what you had open, your projects, then everything.
 *   3 SHELVES   Apple TV's rows: one shelf per place and per project, each "See All".
 *   4 LIST      Finder's list view: name, project, owner, when — for a workspace of hundreds.
 *   5 PROJECTS  Projects lead: each a stack of its files; the four places filter them.
 *   6 PREVIEW   Three panes: places, files, and the chosen file large with its artboards.
 *
 * `state=first` is the first page someone sees before they have a file: the one page all
 * six share, because it is the same moment whatever the layout.
 */

type Design = 1 | 2 | 3 | 4 | 5 | 6;

const DESIGNS: readonly { id: Design; name: string }[] = [
  { id: 1, name: 'Library' },
  { id: 2, name: 'Home' },
  { id: 3, name: 'Shelves' },
  { id: 4, name: 'List' },
  { id: 5, name: 'Projects' },
  { id: 6, name: 'Preview' },
];

export function DashboardsLab() {
  const [params, setParams] = useSearchParams();
  const design = (Number(params.get('design')) || 1) as Design;
  const theme = params.get('theme');
  const first = params.get('state') === 'first';
  const bare = params.get('bare') === '1';

  useEffect(() => {
    if (theme) document.documentElement.dataset.theme = theme;
    else delete document.documentElement.dataset.theme;
  }, [theme]);

  /**
   * Every change to the address in ONE step. Two calls in a row each start from the address
   * as it was when the page drew, so the second quietly undid the first — which is why
   * picking a design did nothing.
   */
  const set = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) next.delete(key);
      else next.set(key, value);
    }
    setParams(next);
  };

  return (
    <div className="h-screen overflow-hidden bg-page text-label">
      {first ? <FirstVisit onDone={() => set({ state: null })} /> : design === 1 ? <LibraryDesign /> : design === 2 ? <HomeDesign /> : design === 3 ? <ShelvesDesign /> : design === 4 ? <ListDesign /> : design === 5 ? <ProjectsDesign /> : <PreviewDesign />}
      {bare ? null : (
        <nav aria-label="Designs" className="glass fixed bottom-[16px] left-1/2 z-50 flex -translate-x-1/2 items-center gap-[2px] rounded-full p-[4px]">
          {DESIGNS.map((one) => (
            <button
              key={one.id}
              type="button"
              onClick={() => set({ design: String(one.id), state: null })}
              className={`h-[28px] rounded-full px-[12px] text-[12px] transition-colors ${!first && design === one.id ? 'bg-fill-selected text-label' : 'text-label-2 hover:text-label'}`}
            >
              {one.id} {one.name}
            </button>
          ))}
          <span className="mx-[4px] h-[16px] w-px bg-separator" />
          <button type="button" onClick={() => set({ state: first ? null : 'first' })} className={`h-[28px] rounded-full px-[12px] text-[12px] ${first ? 'bg-fill-selected text-label' : 'text-label-2 hover:text-label'}`}>
            First visit
          </button>
          <IconButton icon={theme === 'light' ? 'moon' : 'sun'} label="Appearance" onClick={() => set({ theme: theme === 'light' ? 'dark' : 'light' })} />
        </nav>
      )}
    </div>
  );
}

// ── what every design is made of ──────────────────────────────────────────────

/** What a place is showing: which of the four, which project inside it, what is searched. */
function usePlace(start: Scope = 'recent') {
  const [scope, setScope] = useState<Scope>(start);
  const [projectId, setProjectId] = useState<string | null | undefined>(undefined);
  const [search, setSearch] = useState('');
  const files = useMemo(() => filesIn(scope, { ...(projectId !== undefined ? { projectId } : {}), search }), [scope, projectId, search]);
  const title = projectId ? project(projectId)!.name : projectId === null ? 'Not in a Project' : SCOPES.find((one) => one.id === scope)!.label;
  const choose = (next: Scope) => {
    setScope(next);
    setProjectId(undefined);
  };
  const open = (id: string | null) => {
    setScope('workspace');
    setProjectId(id);
  };
  return { scope, choose, projectId, open, search, setSearch, files, title };
}

/** The first artboard on a neutral ground, standing like a page on a table. */
function Thumb({ file, className = '', ground = true, lift = true }: { file: DashFile; className?: string; ground?: boolean; lift?: boolean }) {
  const ratio = ratioOf(file.kind);
  return (
    <span className={`grid place-items-center overflow-hidden ${ground ? 'bg-fill-2 transition-colors duration-150 group-hover:bg-fill-selected' : ''} ${className}`}>
      <img
        src={artSvg(file)}
        alt=""
        draggable={false}
        className={`block max-h-[78%] max-w-[82%] rounded-[3px] shadow-[0_1px_2px_rgb(0_0_0/0.14),0_8px_22px_-8px_rgb(0_0_0/0.45)] ${lift ? 'transition-transform duration-200 group-hover:-translate-y-[2px]' : ''}`}
        style={{ aspectRatio: String(ratio) }}
      />
    </span>
  );
}

/** A project's colour, as a small dot beside its name. */
function Dot({ colour, size = 8 }: { colour: string; size?: number }) {
  return <span className="inline-block shrink-0 rounded-full" style={{ width: size, height: size, background: colour }} />;
}

/** The line under a file's name: its project, and when it last changed. */
function Meta({ file, showProject = true }: { file: DashFile; showProject?: boolean }) {
  const home = project(file.projectId);
  return (
    <span className="flex min-w-0 items-center gap-[6px] text-[11px] text-label-2">
      {showProject && home ? (
        <>
          <Dot colour={home.colour} size={6} />
          <span className="truncate">{home.name}</span>
          <span className="text-label-3">·</span>
        </>
      ) : null}
      <span className="shrink-0">{when(file.opened ?? file.edited)}</span>
    </span>
  );
}

/** Someone else's file carries their face; mine carries nothing, because it goes without saying. */
function Owner({ file, size = 18 }: { file: DashFile; size?: number }) {
  if (file.ownerId === ME) return null;
  const owner = person(file.ownerId);
  return <Avatar name={owner.name} colour={owner.colour} size={size} />;
}

function fileMenu(file: DashFile): MenuEntry[] {
  return [
    { id: 'open', label: 'Open', onSelect: () => {} },
    { id: 'tab', label: 'Open in New Tab', onSelect: () => {} },
    { kind: 'separator' },
    {
      kind: 'submenu',
      id: 'move',
      label: 'Move to Project',
      items: [...PROJECTS.map((one) => ({ id: one.id, label: one.name, checked: file.projectId === one.id, onSelect: () => {} })), { kind: 'separator' as const }, { id: 'none', label: 'No Project', checked: file.projectId === null, onSelect: () => {} }],
    },
    { id: 'share', label: 'Share…', onSelect: () => {} },
    { id: 'rename', label: 'Rename', onSelect: () => {} },
    { id: 'duplicate', label: 'Duplicate', onSelect: () => {} },
    { kind: 'separator' },
    { id: 'delete', label: 'Delete…', icon: 'trash', danger: true, onSelect: () => {} },
  ];
}

/** A file as a picture with its name: the everyday tile. */
function FileCard({ file, big = false, showProject = true, selected = false, onPick }: { file: DashFile; big?: boolean; showProject?: boolean; selected?: boolean; onPick?: () => void }) {
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  return (
    <div className="group/tile relative min-w-0">
      <IconButton
        icon="more"
        label={`More for ${file.name}`}
        className="glass absolute right-[8px] top-[8px] z-10 rounded-full opacity-0 transition-opacity focus-visible:opacity-100 group-hover/tile:opacity-100"
        onClick={(event) => setMenu(event.currentTarget)}
      />
      <button type="button" onClick={onPick} className="group block w-full text-left">
        <Thumb file={file} className={`${big ? 'aspect-[4/3] rounded-[16px]' : 'aspect-[4/3] rounded-[12px]'} ${selected ? 'outline-2 outline-offset-2 outline-accent' : ''}`} />
        <span className="mt-[8px] flex items-center gap-[8px] px-[2px]">
          <span className="min-w-0 flex-1">
            <span className={`block truncate font-medium ${big ? 'text-[14px]' : 'text-[13px]'}`}>{file.name}</span>
            <Meta file={file} showProject={showProject} />
          </span>
          <Owner file={file} />
        </span>
      </button>
      <Menu open={!!menu} anchor={menu} onClose={() => setMenu(null)} align="end" items={fileMenu(file)} />
    </div>
  );
}

function Grid({ files, min = 200, big = false, showProject = true }: { files: readonly DashFile[]; min?: number; big?: boolean; showProject?: boolean }) {
  if (files.length === 0) return <p className="py-[48px] text-center text-[13px] text-label-2">Nothing here.</p>;
  return (
    <ul className="grid gap-x-[20px] gap-y-[24px]" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))` }}>
      {files.map((file) => (
        <li key={file.id}>
          <FileCard file={file} big={big} showProject={showProject} />
        </li>
      ))}
    </ul>
  );
}

/** Search as a field when there is room, and a round button that opens into one when there is not. */
function Search({ value, onChange, compact = false }: { value: string; onChange: (value: string) => void; compact?: boolean }) {
  const [open, setOpen] = useState(!compact || value !== '');
  if (!open)
    return (
      <button type="button" aria-label="Search" {...tip('Search', '⌘F')} onClick={() => setOpen(true)} className="glass grid size-[34px] place-items-center rounded-full text-label-2 hover:text-label">
        <Icon name="search" size={16} />
      </button>
    );
  return (
    <label className="flex h-[32px] w-[220px] items-center gap-[7px] rounded-full bg-fill-1 px-[11px] field-focus">
      <span className="text-label-3">
        <Icon name="search" size={14} />
      </span>
      <input
        autoFocus={compact}
        type="search"
        placeholder="Search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => compact && value === '' && setOpen(false)}
        className="w-0 min-w-0 flex-1 bg-transparent text-[13px] text-label outline-none placeholder:text-label-3"
      />
    </label>
  );
}

function NewFile({ inProject }: { inProject?: Project | null }) {
  return (
    <Button variant="prominent" size="large" icon="plus" {...tip(inProject ? `A new file in ${inProject.name}` : 'A new file', '⌘N')}>
      New File
    </Button>
  );
}

/** Which workspace this is, and the way to another — our own menu, never the sign-in provider's. */
function WorkspaceButton({ wide = false }: { wide?: boolean }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <button type="button" onClick={(event) => setAnchor(event.currentTarget)} className={`flex items-center gap-[8px] rounded-[10px] px-[8px] py-[6px] text-left hover:bg-fill-2 ${wide ? 'w-full' : ''}`}>
        <span className="grid size-[24px] shrink-0 place-items-center rounded-[7px] text-[11px] font-semibold text-white" style={{ background: WORKSPACE.colour }}>
          F
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{WORKSPACE.name}</span>
        <span className="text-label-3">
          <Icon name="chevron-up-down" size={14} />
        </span>
      </button>
      <Menu
        open={!!anchor}
        anchor={anchor}
        onClose={() => setAnchor(null)}
        items={[
          { kind: 'header', label: 'Workspaces' },
          { id: 'flobi', label: WORKSPACE.name, checked: true, onSelect: () => {} },
          { id: 'personal', label: 'Olive & Co', onSelect: () => {} },
          { kind: 'separator' },
          { id: 'settings', label: 'Workspace Settings…', onSelect: () => {} },
          { id: 'account', label: 'Account…', onSelect: () => {} },
        ]}
      />
    </>
  );
}

/** One row of a source list. */
function SourceRow({ glyph, label, count, active, onClick, dot }: { glyph?: IconName; label: string; count?: number; active: boolean; onClick: () => void; dot?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-[30px] w-full items-center gap-[9px] rounded-[8px] px-[9px] text-left text-[13px] transition-colors ${active ? 'bg-fill-selected text-label' : 'text-label hover:bg-fill-2'}`}
    >
      {dot ? (
        <span className="grid w-[16px] place-items-center">
          <Dot colour={dot} />
        </span>
      ) : glyph ? (
        <span className={active ? 'text-accent-text' : 'text-label-2'}>
          <Icon name={glyph} size={16} solid={active} />
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined ? <span className="text-[12px] tabular-nums text-label-3">{count}</span> : null}
    </button>
  );
}

/** The inset source list: the four places, then Projects, then the workspace at the foot. */
function SourceList({ place, width = 232 }: { place: ReturnType<typeof usePlace>; width?: number }) {
  return (
    <aside className="m-[10px] mr-0 flex shrink-0 flex-col rounded-[14px] bg-fill-3 p-[8px]" style={{ width }}>
      <div className="flex items-center gap-[8px] px-[9px] pb-[10px] pt-[6px]">
        <span className="grid size-[22px] place-items-center rounded-[6px] bg-accent text-on-accent">
          <Icon name="pen" size={13} />
        </span>
        <span className="text-[14px] font-semibold">Artwork</span>
      </div>
      <div className="flex flex-col gap-[1px]">
        {SCOPES.map((one) => (
          <SourceRow key={one.id} glyph={one.glyph} label={one.label} active={place.scope === one.id && place.projectId === undefined} onClick={() => place.choose(one.id)} />
        ))}
      </div>
      <div className="mt-[16px] flex items-center justify-between px-[9px] pb-[4px]">
        <span className="text-[11px] font-semibold text-label-3">Projects</span>
        <IconButton icon="plus" label="New Project" size={20} />
      </div>
      <div className="flex flex-col gap-[1px]">
        {PROJECTS.map((one) => (
          <SourceRow key={one.id} dot={one.colour} label={one.name} count={filesOfProject(one.id).length} active={place.projectId === one.id} onClick={() => place.open(one.id)} />
        ))}
      </div>
      <div className="mt-auto pt-[10px]">
        <WorkspaceButton wide />
      </div>
    </aside>
  );
}

/** A title with how many it holds. */
function Title({ children, count, size = 26 }: { children: ReactNode; count?: number; size?: number }) {
  return (
    <h1 className="flex items-baseline gap-[10px] font-bold tracking-[-0.01em]" style={{ fontSize: size, lineHeight: `${size + 6}px` }}>
      {children}
      {count !== undefined ? <span className="text-[15px] font-medium tabular-nums text-label-3">{count}</span> : null}
    </h1>
  );
}

// ── 1 · Library ───────────────────────────────────────────────────────────────

/** Photos: a source list down the side and the work as big pictures. */
function LibraryDesign() {
  const place = usePlace();
  return (
    <div className="flex h-full">
      <SourceList place={place} />
      <main className="min-w-0 flex-1 overflow-y-auto ui-scroll">
        <header className="sticky top-0 z-20 flex items-center gap-[12px] bg-page/90 px-[32px] pb-[18px] pt-[28px] backdrop-blur-xl">
          <div className="min-w-0 flex-1">
            <Title count={place.files.length}>{place.title}</Title>
          </div>
          <Search value={place.search} onChange={place.setSearch} compact />
          <NewFile inProject={place.projectId ? project(place.projectId) : null} />
        </header>
        <div className="px-[32px] pb-[96px]">
          <Grid files={place.files} min={220} big />
        </div>
      </main>
    </div>
  );
}

// ── 2 · Home ──────────────────────────────────────────────────────────────────

/** Pages' opening screen: pick up where you were, your projects, then everything. */
function HomeDesign() {
  const [scope, setScope] = useState<Exclude<Scope, 'recent'>>('mine');
  const [search, setSearch] = useState('');
  const recent = filesIn('recent').slice(0, 4);
  const below = filesIn(scope, { search });
  return (
    <div className="h-full overflow-y-auto ui-scroll">
      <div className="mx-auto max-w-[1180px] px-[40px] pb-[96px]">
        <header className="flex items-center gap-[12px] pb-[24px] pt-[32px]">
          <div className="min-w-0 flex-1">
            <Title size={30}>Artwork</Title>
          </div>
          <WorkspaceButton />
          <Search value={search} onChange={setSearch} compact />
          <NewFile />
        </header>

        <section>
          <SectionHead title="Recents" />
          <ul className="grid grid-cols-4 gap-[20px]">
            {recent.map((file) => (
              <li key={file.id}>
                <FileCard file={file} big />
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-[40px]">
          <SectionHead title="Projects" action="New Project" />
          <ul className="grid grid-cols-4 gap-[20px]">
            {PROJECTS.map((one) => (
              <li key={one.id}>
                <ProjectStack project={one} />
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-[40px]">
          <div className="mb-[16px] flex items-center gap-[12px]">
            <SegmentedControl
              label="Whose files"
              className="w-[380px]"
              value={scope}
              onChange={setScope}
              segments={[
                { value: 'mine', label: 'Owned by Me' },
                { value: 'shared', label: 'Shared with Me' },
                { value: 'workspace', label: 'Workspace' },
              ]}
            />
            <span className="text-[12px] tabular-nums text-label-3">{below.length}</span>
          </div>
          <Grid files={below} min={190} />
        </section>
      </div>
    </div>
  );
}

function SectionHead({ title, action, onAction, count }: { title: string; action?: string; onAction?: () => void; count?: number }) {
  return (
    <div className="mb-[14px] flex items-baseline gap-[8px]">
      <h2 className="text-[17px] font-semibold">{title}</h2>
      {count !== undefined ? <span className="text-[13px] tabular-nums text-label-3">{count}</span> : null}
      <span className="flex-1" />
      {action ? (
        <button type="button" onClick={onAction} className="flex items-center gap-[2px] text-[13px] text-accent-text hover:underline">
          {action}
          {action === 'See All' ? <Icon name="chevron-right" size={14} /> : null}
        </button>
      ) : null}
    </div>
  );
}

/** A project as the pile of its newest three files, fanned a little, so it reads as a folder of work. */
function ProjectStack({ project: one, onOpen }: { project: Project; onOpen?: () => void }) {
  const files = filesOfProject(one.id);
  const top = files.slice(0, 3);
  const people = [...new Set(files.map((file) => file.ownerId))];
  return (
    <button type="button" onClick={onOpen} className="group block w-full text-left">
      <span className="relative block aspect-[4/3] overflow-hidden rounded-[16px] bg-fill-2 transition-colors group-hover:bg-fill-selected">
        {top
          .slice()
          .reverse()
          .map((file, index, all) => {
            const depth = all.length - 1 - index;
            return (
              <img
                key={file.id}
                src={artSvg(file)}
                alt=""
                draggable={false}
                className="absolute left-1/2 top-1/2 max-h-[62%] max-w-[56%] rounded-[3px] shadow-[0_1px_2px_rgb(0_0_0/0.14),0_8px_22px_-8px_rgb(0_0_0/0.45)] transition-transform duration-300"
                style={{
                  aspectRatio: String(ratioOf(file.kind)),
                  transform: `translate(-50%, -50%) translate(${(depth - 1) * 22}px, ${depth * -4}px) rotate(${(depth - 1) * 6}deg) scale(${1 - depth * 0.06})`,
                }}
              />
            );
          })}
      </span>
      <span className="mt-[8px] flex items-center gap-[8px] px-[2px]">
        <Dot colour={one.colour} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-medium">{one.name}</span>
          <span className="block text-[11px] text-label-2">
            {files.length} files · {when(files[0]?.edited ?? 0)}
          </span>
        </span>
        <span className="flex -space-x-[6px]">
          {people.slice(0, 3).map((id) => (
            <Avatar key={id} name={person(id).name} colour={person(id).colour} size={18} />
          ))}
        </span>
      </span>
    </button>
  );
}

// ── 3 · Shelves ───────────────────────────────────────────────────────────────

/** Apple TV: a shelf for each place and each project, each opening into all of it. */
function ShelvesDesign() {
  const [search, setSearch] = useState('');
  const [all, setAll] = useState<{ title: string; files: DashFile[] } | null>(null);
  const shelves: { title: string; files: DashFile[]; dot?: string }[] = [
    { title: 'Recents', files: filesIn('recent', { search }) },
    { title: 'Shared with Me', files: filesIn('shared', { search }) },
    ...PROJECTS.map((one) => ({ title: one.name, dot: one.colour, files: filesIn('workspace', { projectId: one.id, search }) })),
    { title: 'Owned by Me', files: filesIn('mine', { search }) },
    { title: 'Workspace', files: filesIn('workspace', { search }) },
  ].filter((shelf) => shelf.files.length > 0);

  return (
    <div className="h-full overflow-y-auto ui-scroll">
      <header className="sticky top-0 z-20 flex items-center gap-[12px] bg-page/90 px-[40px] pb-[16px] pt-[28px] backdrop-blur-xl">
        {all ? <IconButton icon="back" label="Back" size={30} onClick={() => setAll(null)} /> : null}
        <div className="min-w-0 flex-1">
          <Title count={all?.files.length}>{all ? all.title : 'Artwork'}</Title>
        </div>
        {all ? null : <WorkspaceButton />}
        <Search value={search} onChange={setSearch} compact />
        <NewFile />
      </header>
      {all ? (
        <div className="px-[40px] pb-[96px]">
          <Grid files={all.files} min={210} big />
        </div>
      ) : (
        <div className="pb-[96px]">
          {shelves.map((shelf) => (
            <section key={shelf.title} className="mt-[18px]">
              <div className="flex items-center gap-[8px] px-[40px]">
                {shelf.dot ? <Dot colour={shelf.dot} /> : null}
                <h2 className="text-[17px] font-semibold">{shelf.title}</h2>
                <span className="flex-1" />
                <button type="button" onClick={() => setAll(shelf)} className="flex items-center text-[13px] text-accent-text hover:underline">
                  See All
                  <Icon name="chevron-right" size={14} />
                </button>
              </div>
              <ul className="ui-scroll flex gap-[18px] overflow-x-auto px-[40px] pb-[6px] pt-[12px]">
                {shelf.files.map((file) => (
                  <li key={file.id} className="w-[232px] shrink-0">
                    <FileCard file={file} showProject={!shelf.dot} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 4 · List ──────────────────────────────────────────────────────────────────

type SortKey = 'name' | 'project' | 'owner' | 'when';

/** Finder's list view: everything in columns, sorted by any of them. */
function ListDesign() {
  const place = usePlace('workspace');
  const [sort, setSort] = useState<{ key: SortKey; up: boolean }>({ key: 'when', up: true });
  const [picked, setPicked] = useState<string | null>(null);
  const [asGrid, setAsGrid] = useState(false);
  const rows = useMemo(() => {
    const by = (file: DashFile) =>
      sort.key === 'name' ? file.name : sort.key === 'project' ? (project(file.projectId)?.name ?? '~') : sort.key === 'owner' ? person(file.ownerId).name : (file.opened ?? file.edited);
    return [...place.files].sort((a, b) => {
      const x = by(a);
      const y = by(b);
      const order = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
      return sort.up ? order : -order;
    });
  }, [place.files, sort]);

  const head = (key: SortKey, label: string, className: string) => (
    <button type="button" onClick={() => setSort((now) => ({ key, up: now.key === key ? !now.up : true }))} className={`flex items-center gap-[4px] text-left ${className} ${sort.key === key ? 'text-label' : ''}`}>
      {label}
      {sort.key === key ? (
        <span className={sort.up ? '' : 'rotate-180'}>
          <Icon name="chevron" size={11} />
        </span>
      ) : null}
    </button>
  );

  return (
    <div className="flex h-full">
      <SourceList place={place} width={216} />
      <main className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-[12px] px-[28px] pb-[14px] pt-[24px]">
          <div className="min-w-0 flex-1">
            <Title size={22} count={rows.length}>
              {place.title}
            </Title>
          </div>
          <SegmentedControl
            label="View"
            size="small"
            value={asGrid ? 'grid' : 'list'}
            onChange={(value) => setAsGrid(value === 'grid')}
            segments={[
              { value: 'grid', icon: 'grid', tip: 'As Pictures' },
              { value: 'list', icon: 'list', tip: 'As List' },
            ]}
          />
          <Search value={place.search} onChange={place.setSearch} />
          <NewFile inProject={place.projectId ? project(place.projectId) : null} />
        </header>
        {asGrid ? (
          <div className="ui-scroll flex-1 overflow-y-auto px-[28px] pb-[96px]">
            <Grid files={rows} min={170} />
          </div>
        ) : (
          <div className="ui-scroll flex-1 overflow-y-auto px-[20px] pb-[96px]">
            <div className="sticky top-0 z-10 grid grid-cols-[minmax(0,2.4fr)_minmax(0,1.3fr)_minmax(0,1fr)_110px_70px] items-center gap-[12px] border-b border-separator bg-page px-[8px] py-[8px] text-[11px] font-medium text-label-2">
              {head('name', 'Name', '')}
              {head('project', 'Project', '')}
              {head('owner', 'Owner', '')}
              {head('when', place.scope === 'recent' ? 'Opened' : 'Modified', '')}
              <span className="text-right">Artboards</span>
            </div>
            {rows.map((file, index) => {
              const home = project(file.projectId);
              const owner = person(file.ownerId);
              return (
                <button
                  key={file.id}
                  type="button"
                  onClick={() => setPicked(file.id)}
                  onDoubleClick={() => {}}
                  className={`group grid w-full grid-cols-[minmax(0,2.4fr)_minmax(0,1.3fr)_minmax(0,1fr)_110px_70px] items-center gap-[12px] rounded-[8px] px-[8px] py-[6px] text-left text-[13px] ${
                    picked === file.id ? 'bg-accent-soft' : index % 2 ? 'bg-fill-3' : ''
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-[10px]">
                    <Thumb file={file} className="size-[34px] shrink-0 rounded-[6px]" lift={false} />
                    <span className="truncate font-medium">{file.name}</span>
                  </span>
                  <span className="flex min-w-0 items-center gap-[7px] text-label-2">
                    {home ? (
                      <>
                        <Dot colour={home.colour} size={7} />
                        <span className="truncate">{home.name}</span>
                      </>
                    ) : (
                      <span className="text-label-3">—</span>
                    )}
                  </span>
                  <span className="flex min-w-0 items-center gap-[7px] text-label-2">
                    <Avatar name={owner.name} colour={owner.colour} size={18} />
                    <span className="truncate">{file.ownerId === ME ? 'Me' : owner.name}</span>
                  </span>
                  <span className="text-label-2">{when(file.opened ?? file.edited)}</span>
                  <span className="text-right tabular-nums text-label-2">{file.artboards}</span>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

// ── 5 · Projects ──────────────────────────────────────────────────────────────

/** Projects lead: the work is where it belongs; the four places only say whose of it to show. */
function ProjectsDesign() {
  const [scope, setScope] = useState<Scope>('workspace');
  const [openId, setOpenId] = useState<string | null | undefined>(undefined);
  const [search, setSearch] = useState('');
  const recent = filesIn('recent').slice(0, 6);
  const groups: { id: string | null; name: string; colour: string | null }[] = [...PROJECTS.map((one) => ({ id: one.id, name: one.name, colour: one.colour })), { id: null, name: 'Not in a Project', colour: null }];

  if (openId !== undefined) {
    const one = project(openId);
    const files = filesIn(scope, { projectId: openId, search });
    return (
      <div className="h-full overflow-y-auto ui-scroll">
        <div className="mx-auto max-w-[1180px] px-[40px] pb-[96px]">
          <header className="flex items-center gap-[12px] pb-[20px] pt-[28px]">
            <IconButton icon="back" label="All Projects" size={30} onClick={() => setOpenId(undefined)} />
            {one ? <Dot colour={one.colour} size={10} /> : null}
            <div className="min-w-0 flex-1">
              <Title count={files.length}>{one?.name ?? 'Not in a Project'}</Title>
            </div>
            <ScopeMenu scope={scope} onChange={setScope} />
            <Search value={search} onChange={setSearch} compact />
            <NewFile inProject={one} />
          </header>
          <Grid files={files} min={220} big showProject={false} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <aside className="m-[10px] mr-0 flex w-[280px] shrink-0 flex-col rounded-[14px] bg-fill-3 p-[12px]">
        <div className="flex items-center justify-between px-[4px] pb-[10px] pt-[4px]">
          <span className="text-[14px] font-semibold">Recents</span>
        </div>
        <ul className="flex flex-col gap-[2px]">
          {recent.map((file) => (
            <li key={file.id}>
              <button type="button" className="group flex w-full items-center gap-[10px] rounded-[10px] p-[6px] text-left hover:bg-fill-2">
                <Thumb file={file} className="size-[44px] shrink-0 rounded-[8px]" lift={false} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium">{file.name}</span>
                  <Meta file={file} />
                </span>
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-auto pt-[10px]">
          <WorkspaceButton wide />
        </div>
      </aside>
      <main className="min-w-0 flex-1 overflow-y-auto ui-scroll">
        <header className="sticky top-0 z-20 flex items-center gap-[12px] bg-page/90 px-[32px] pb-[18px] pt-[28px] backdrop-blur-xl">
          <div className="min-w-0 flex-1">
            <Title count={PROJECTS.length}>Projects</Title>
          </div>
          <ScopeMenu scope={scope} onChange={setScope} />
          <Search value={search} onChange={setSearch} compact />
          <Button size="large">New Project</Button>
          <NewFile />
        </header>
        <div className="flex flex-col gap-[28px] px-[32px] pb-[96px]">
          {groups.map((group) => {
            const files = filesIn(scope, { projectId: group.id, search });
            if (files.length === 0) return null;
            return (
              <section key={group.id ?? 'none'} className="rounded-[18px] bg-fill-3 p-[16px]">
                <button type="button" onClick={() => setOpenId(group.id)} className="mb-[12px] flex w-full items-center gap-[8px] text-left">
                  {group.colour ? <Dot colour={group.colour} size={10} /> : <span className="text-label-3"><Icon name="document" size={14} /></span>}
                  <span className="text-[15px] font-semibold">{group.name}</span>
                  <span className="text-[13px] tabular-nums text-label-3">{files.length}</span>
                  <span className="flex-1" />
                  <span className="flex items-center text-[13px] text-accent-text">
                    Open
                    <Icon name="chevron-right" size={14} />
                  </span>
                </button>
                <ul className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-[14px]">
                  {files.slice(0, 5).map((file) => (
                    <li key={file.id}>
                      <FileCard file={file} showProject={false} />
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </main>
    </div>
  );
}

/** Whose files: one pop-up rather than four buttons, since it is set once and left. */
function ScopeMenu({ scope, onChange }: { scope: Scope; onChange: (scope: Scope) => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const now = SCOPES.find((one) => one.id === scope)!;
  return (
    <>
      <Button size="large" onClick={(event) => setAnchor(event.currentTarget)}>
        <Icon name={now.glyph} size={15} />
        {now.label}
        <span className="text-label-3">
          <Icon name="chevron-up-down" size={13} />
        </span>
      </Button>
      <Menu
        open={!!anchor}
        anchor={anchor}
        onClose={() => setAnchor(null)}
        align="end"
        items={SCOPES.map((one) => ({ id: one.id, label: one.label, icon: one.glyph, checked: one.id === scope, onSelect: () => onChange(one.id) }))}
      />
    </>
  );
}

// ── 6 · Preview ───────────────────────────────────────────────────────────────

/** Three panes: the places, the files, and the one chosen, large, with what it holds. */
function PreviewDesign() {
  const place = usePlace();
  const [picked, setPicked] = useState<string>(place.files[0]?.id ?? FILES[0]!.id);
  const file = FILES.find((one) => one.id === picked) ?? place.files[0] ?? FILES[0]!;
  const home = project(file.projectId);
  const owner = person(file.ownerId);
  const people = [owner, ...PEOPLE_WITH_ACCESS.filter((one) => one.id !== owner.id).slice(0, file.sharedWithMe ? 2 : 3)];
  const boards = Array.from({ length: Math.min(file.artboards, 8) }, (_, index) => ({ ...file, id: `${file.id}-${index}`, palette: rotate(file.palette, index) }));

  return (
    <div className="flex h-full">
      <SourceList place={place} width={216} />
      <main className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-[12px] px-[24px] pb-[14px] pt-[24px]">
          <div className="min-w-0 flex-1">
            <Title size={22} count={place.files.length}>
              {place.title}
            </Title>
          </div>
          <Search value={place.search} onChange={place.setSearch} compact />
          <NewFile inProject={place.projectId ? project(place.projectId) : null} />
        </header>
        <div className="ui-scroll flex-1 overflow-y-auto px-[24px] pb-[96px] pt-[4px]">
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-[16px] px-[4px]">
            {place.files.map((one) => (
              <li key={one.id}>
                <FileCard file={one} selected={one.id === file.id} onPick={() => setPicked(one.id)} />
              </li>
            ))}
          </ul>
        </div>
      </main>
      <aside className="m-[10px] ml-0 flex w-[340px] shrink-0 flex-col overflow-hidden rounded-[14px] bg-fill-3">
        <Thumb file={file} className="aspect-[4/3] w-full" lift={false} />
        <div className="ui-scroll flex-1 overflow-y-auto p-[16px]">
          <h2 className="text-[17px] font-semibold leading-[22px]">{file.name}</h2>
          <p className="mt-[2px] text-[12px] text-label-2">
            {file.artboards} artboard{file.artboards === 1 ? '' : 's'} · edited {when(file.edited).toLowerCase()}
          </p>
          <div className="mt-[14px] flex gap-[8px]">
            <Button variant="prominent" size="large" className="flex-1">
              Open
            </Button>
            <Button size="large">Share…</Button>
            <IconButton icon="more" label="More" size={34} />
          </div>
          <ul className="ui-scroll mt-[16px] flex gap-[8px] overflow-x-auto pb-[4px]">
            {boards.map((board) => (
              <li key={board.id} className="w-[64px] shrink-0">
                <Thumb file={board} className="aspect-square rounded-[8px]" lift={false} />
              </li>
            ))}
          </ul>
          <dl className="mt-[16px] grid grid-cols-[88px_minmax(0,1fr)] gap-y-[10px] text-[13px]">
            <dt className="text-label-2">Project</dt>
            <dd className="flex min-w-0 items-center gap-[7px]">
              {home ? (
                <>
                  <Dot colour={home.colour} />
                  <span className="truncate">{home.name}</span>
                </>
              ) : (
                <button type="button" className="text-accent-text hover:underline">
                  Add to a Project
                </button>
              )}
            </dd>
            <dt className="text-label-2">Owner</dt>
            <dd className="flex items-center gap-[7px]">
              <Avatar name={owner.name} colour={owner.colour} size={18} />
              {file.ownerId === ME ? 'Me' : owner.name}
            </dd>
            <dt className="text-label-2">People</dt>
            <dd className="flex -space-x-[6px]">
              {people.map((one) => (
                <Avatar key={one.id} name={one.name} colour={one.colour} size={22} />
              ))}
            </dd>
          </dl>
        </div>
      </aside>
    </div>
  );
}

const PEOPLE_WITH_ACCESS = ['tarek', 'dana', 'farah', 'mira'].map(person);
const rotate = <T,>(list: readonly [T, T, T, T], by: number): [T, T, T, T] => [0, 1, 2, 3].map((i) => list[(i + by) % 4]!) as [T, T, T, T];

// ── the first page ────────────────────────────────────────────────────────────

/**
 * What someone sees before they have made anything: a stage that plays once and rests on
 * its last frame, a title, one line, one action. The same in every design.
 */
function FirstVisit({ onDone }: { onDone: () => void }) {
  const art = [FILES[0]!, FILES[3]!, FILES[6]!, FILES[1]!, FILES[2]!];
  return (
    <div className="grid h-full place-items-center px-[24px]">
      <style>{`
        @keyframes dash-rise { from { opacity: 0; transform: var(--from); } to { opacity: 1; transform: var(--to); } }
        @keyframes dash-fade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .dash-anim { animation: none !important; opacity: 1 !important; transform: var(--to) !important; } }
      `}</style>
      <div className="flex max-w-[560px] flex-col items-center text-center">
        <div className="relative mb-[36px] h-[240px] w-[520px]">
          {art.map((file, index) => {
            const x = (index - 2) * 96;
            const turn = (index - 2) * 7;
            const to = `translate(-50%, -50%) translate(${x}px, ${Math.abs(index - 2) * 14}px) rotate(${turn}deg)`;
            return (
              <img
                key={file.id}
                src={artSvg(file)}
                alt=""
                className="dash-anim absolute left-1/2 top-1/2 max-h-[190px] max-w-[160px] rounded-[4px] shadow-[0_2px_4px_rgb(0_0_0/0.15),0_18px_40px_-12px_rgb(0_0_0/0.55)]"
                style={
                  {
                    aspectRatio: String(ratioOf(file.kind)),
                    zIndex: 10 - Math.abs(index - 2),
                    opacity: 0,
                    '--from': 'translate(-50%, -40%) scale(0.92)',
                    '--to': to,
                    transform: to,
                    animation: `dash-rise 700ms cubic-bezier(0.2, 0.9, 0.25, 1) ${120 + Math.abs(index - 2) * 110}ms forwards`,
                  } as CSSProperties
                }
              />
            );
          })}
        </div>
        <h1 className="dash-anim text-[28px] font-bold tracking-[-0.01em]" style={{ opacity: 0, animation: 'dash-fade 500ms ease-out 520ms forwards' } as CSSProperties}>
          Artwork
        </h1>
        <p className="dash-anim mt-[6px] text-[15px] text-label-2" style={{ opacity: 0, animation: 'dash-fade 500ms ease-out 620ms forwards' } as CSSProperties}>
          Posters, posts and decks on one endless canvas, with your team.
        </p>
        <div className="dash-anim mt-[22px]" style={{ opacity: 0, animation: 'dash-fade 500ms ease-out 720ms forwards' } as CSSProperties}>
          <Button variant="prominent" size="large" icon="plus" onClick={onDone}>
            New File
          </Button>
        </div>
      </div>
    </div>
  );
}

